import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Filter, Edit, Eye, Trash2, Shield, GitBranch, FileText, MapPin, TrendingUp } from 'lucide-react';
import ProtocolForm from './ProtocolForm';
import ProtocolList from './ProtocolList';
import ProtocolViewModal from './ProtocolViewModal';
import ProtocolVersionControl from './ProtocolVersionControl';
import ProtocolTemplateLibrary from './ProtocolTemplateLibrary';
import ProtocolAnalyticsDashboard from './ProtocolAnalyticsDashboard';
import { useProtocols, useProtocolMutations } from '../../hooks/useProtocols';
import { protocolService } from '../../services/protocolService';
import Modal from '../ui/Modal';
import './ProtocolConfiguration.css';

const ProtocolConfiguration = ({ organizationId }) => {
  const { t } = useTranslation('protocols');
  const [activeView, setActiveView] = useState('list'); // 'list', 'form', 'version', 'template', 'analytics'
  const [editingProtocol, setEditingProtocol] = useState(null);
  const [viewingProtocol, setViewingProtocol] = useState(null); // For read-only view
  const [selectedProtocol, setSelectedProtocol] = useState(null); // For version control or assignment
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [cleaningUp, setCleaningUp] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { protocolId, protocolName }
  const [cleanupConfirm, setCleanupConfirm] = useState(false);
  const [messageModal, setMessageModal] = useState(null); // { type: 'success'|'error', message }

  const { protocols, loading, refresh } = useProtocols();
  const { 
    createProtocol, 
    updateProtocol, 
    deleteProtocol 
  } = useProtocolMutations();

  // Filter protocols based on search and filters
  const filteredProtocols = protocols.filter(protocol => {
    const matchesSearch = !searchQuery || 
      protocol.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      protocol.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCategory = !filterCategory || protocol.category === filterCategory;
    const matchesStatus = !filterStatus || protocol.status === filterStatus;
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Handle creating new protocol
  const handleCreateProtocol = () => {
    setEditingProtocol(null);
    setActiveView('form');
  };

  // Handle editing existing protocol
  const handleEditProtocol = (protocol) => {
    setEditingProtocol(protocol);
    setActiveView('form');
  };

  // Handle viewing protocol details (read-only)
  const handleViewProtocol = (protocol) => {
    setViewingProtocol(protocol);
    // activeView stays 'list' - modal overlay
  };

  // Handle deleting protocol - now checks dependencies first
  const handleDeleteProtocol = async (protocolId, protocolName) => {
    try {
      // Check dependencies before showing delete confirmation
      const { dependencyCheckService } = await import('../../services/dependencyCheckService');
      const dependencyCheck = await dependencyCheckService.checkProtocolDeletion(protocolId);
      
      if (!dependencyCheck.canDelete && dependencyCheck.blockingDependencies.length > 0) {
        // Block deletion - show detailed dependency information
        const dependencyMessages = dependencyCheck.blockingDependencies.map(d => `• ${d.message}`).join('\n');
        const warningMessages = dependencyCheck.nonBlockingWarnings.map(w => `• ${w.message}`).join('\n');
        
        let errorMessage = `Cannot delete protocol "${protocolName}":\n\nBlocking Dependencies:\n${dependencyMessages}`;
        if (warningMessages) {
          errorMessage += `\n\nWarnings:\n${warningMessages}`;
        }
        errorMessage += '\n\nPlease remove or update these dependencies before deleting.';
        
        setMessageModal({ 
          type: 'error', 
          message: errorMessage,
          title: 'Deletion Blocked - Dependencies Found'
        });
        return;
      }
      
      // Show warnings if any (non-blocking)
      if (dependencyCheck.warnings && dependencyCheck.warnings.length > 0) {
        const warningMessages = dependencyCheck.warnings.map(w => `• ${w.message}`).join('\n');
        setMessageModal({ 
          type: 'warning', 
          message: `⚠️ Warning: Deleting "${protocolName}" will affect:\n\n${warningMessages}\n\nDo you want to continue?`,
          title: 'Deletion Warning',
          onConfirm: () => {
            setDeleteConfirm({ protocolId, protocolName, warnings: dependencyCheck.warnings });
          },
          onCancel: () => {
            setMessageModal(null);
          }
        });
        return;
      }
      
      // No dependencies, proceed with normal confirmation
      setDeleteConfirm({ protocolId, protocolName });
    } catch (error) {
      console.error('Error checking protocol dependencies:', error);
      setMessageModal({ 
        type: 'error', 
        message: `Failed to verify dependencies: ${error.message}. Deletion blocked for safety.`,
        title: 'Safety Check Failed'
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    
    try {
      await deleteProtocol(deleteConfirm.protocolId, 'current-user'); // TODO: Get actual userId from auth
      refresh(); // Refresh the protocol list
      setDeleteConfirm(null);
      setMessageModal({ type: 'success', message: 'Protocol deleted successfully!' });
    } catch (error) {
      console.error('Error deleting protocol:', error);
      setMessageModal({ 
        type: 'error', 
        message: error.message || 'Failed to delete protocol. Please try again.',
        title: 'Deletion Failed'
      });
      setDeleteConfirm(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  // Handle form save
  const handleFormSave = (protocolData) => {
    console.log('Protocol saved:', protocolData);
    refresh(); // Refresh the protocol list
    setActiveView('list');
    setEditingProtocol(null);
  };

  // Handle form cancel
  const handleFormCancel = () => {
    setActiveView('list');
    setEditingProtocol(null);
  };

  // Handle cleanup duplicates
  const handleCleanupDuplicates = () => {
    setCleanupConfirm(true);
  };

  const handleCleanupConfirm = async () => {
    try {
      setCleaningUp(true);
      const duplicatesRemoved = await protocolService.cleanupDuplicateProtocols();
      setCleanupConfirm(false);
      setMessageModal({ type: 'success', message: `Cleanup complete! Removed ${duplicatesRemoved} duplicate protocols.` });
      refresh(); // Refresh the protocol list
    } catch (error) {
      console.error('Error during cleanup:', error);
      setCleanupConfirm(false);
      setMessageModal({ type: 'error', message: 'Failed to cleanup duplicates. Please try again.' });
    } finally {
      setCleaningUp(false);
    }
  };

  const handleCleanupCancel = () => {
    setCleanupConfirm(false);
  };


  // Handle close view modal
  const handleCloseViewModal = () => {
    setViewingProtocol(null);
  };

  // Get unique categories for filter
  const categories = [...new Set(protocols.map(p => p.category))];

  // Handle viewing version control
  const handleVersionControl = (protocol) => {
    setSelectedProtocol(protocol);
    setActiveView('version');
  };

  // Handle viewing templates (redirects to menu entry)
  const handleTemplates = () => {
    // Note: Template Library is now accessible via menu entry "Template Library" under Protocols
    // This button is kept for quick access but users should use the menu for better UX
    setActiveView('template');
  };


  // Render different views
  if (activeView === 'form') {
    return (
      <ProtocolForm
        protocolId={editingProtocol?.firestoreId || editingProtocol?.id}
        organizationId={organizationId}
        onSave={handleFormSave}
        onCancel={handleFormCancel}
        onClose={handleFormCancel}
      />
    );
  }

  if (activeView === 'version' && selectedProtocol) {
    return (
      <ProtocolVersionControl
        protocolId={selectedProtocol.firestoreId || selectedProtocol.id}
        onBack={() => {
          setActiveView('list');
          setSelectedProtocol(null);
        }}
      />
    );
  }

  if (activeView === 'template') {
    return (
      <ProtocolTemplateLibrary
        organizationId={organizationId}
        onSelectTemplate={(templateData) => {
          setEditingProtocol({ ...templateData, isNew: true });
          setActiveView('form');
        }}
      />
    );
  }

  if (activeView === 'analytics') {
    return (
      <ProtocolAnalyticsDashboard
        onBack={() => setActiveView('list')}
      />
    );
  }


  return (
    <div className="protocol-configuration" style={{ width: '100%', maxWidth: 'none', margin: 0 }}>
      <div className="protocol-configuration-header">
        <div>
          <h1>{t('protocolConfiguration')}</h1>
          <p className="subtitle">{t('protocolConfigurationSubtitle')}</p>
        </div>
        <div className="header-actions">
          <button
            onClick={() => setActiveView('analytics')}
            className="btn btn-secondary btn-icon"
            title={t('viewProtocolAnalytics')}
          >
            <TrendingUp size={16} />
            {t('analytics')}
          </button>
          <button
            onClick={handleTemplates}
            className="btn btn-secondary btn-icon"
            title={t('browseTemplates')}
          >
            <FileText size={16} />
            {t('templates')}
          </button>
          <button
            onClick={handleCleanupDuplicates}
            disabled={cleaningUp}
            className="btn btn-secondary btn-icon"
            title={t('removeDuplicateProtocols')}
          >
            <Shield size={16} />
            {cleaningUp ? t('cleaning') : t('cleanup')}
          </button>
          <button
            onClick={handleCreateProtocol}
            className="btn btn-primary btn-icon"
          >
            <Plus size={16} />
            {t('createNewProtocol')}
          </button>
        </div>
      </div>

      <div className="protocol-configuration-filters">
        <div className="search-bar">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder={t('searchProtocols')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

        <div className="filter-group">
          <Filter size={16} className="filter-icon" />
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('allCategories')}</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="">{t('allStatuses')}</option>
            <option value="draft">{t('draft')}</option>
            <option value="review">{t('underReview')}</option>
            <option value="approved">{t('approved')}</option>
            <option value="active">{t('active')}</option>
            <option value="archived">{t('archived')}</option>
          </select>
        </div>
      </div>

      <div className="protocol-configuration-content">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>{t('loadingProtocols')}</p>
          </div>
        ) : filteredProtocols.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📋</div>
            <h3>{t('noProtocolsFoundTitle')}</h3>
            <p>
              {protocols.length === 0 
                ? t('noProtocolsYet')
                : t('noMatchSearchFilter')
              }
            </p>
            {protocols.length === 0 && (
              <button
                onClick={handleCreateProtocol}
                className="btn btn-primary"
              >
                <Plus size={16} />
                {t('createFirstProtocolButton')}
              </button>
            )}
          </div>
        ) : (
          <ProtocolList
            showHeader={false}
            onEdit={handleEditProtocol}
            onView={handleViewProtocol}
            onDelete={(protocolId, protocolName) => handleDeleteProtocol(protocolId, protocolName)}
            onVersionControl={handleVersionControl}
          />
        )}
      </div>

      {/* View Modal (Read-only) */}
      {viewingProtocol && (
        <ProtocolViewModal
          protocol={viewingProtocol}
          onClose={handleCloseViewModal}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteConfirm}
        onClose={handleDeleteCancel}
        title="Delete Protocol"
        size="small"
      >
        <div style={{ padding: '20px' }}>
          <p>Are you sure you want to delete the protocol "{deleteConfirm?.protocolName}"?</p>
          <p style={{ color: '#e53e3e', marginTop: '10px' }}>This action cannot be undone.</p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-secondary"
              onClick={handleDeleteCancel}
            >
              Cancel
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDeleteConfirm}
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>

      {/* Cleanup Confirmation Modal */}
      <Modal
        isOpen={cleanupConfirm}
        onClose={handleCleanupCancel}
        title="Cleanup Duplicate Protocols"
        size="small"
      >
        <div style={{ padding: '20px' }}>
          <p>This will remove duplicate protocols. Are you sure?</p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-secondary"
              onClick={handleCleanupCancel}
              disabled={cleaningUp}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              onClick={handleCleanupConfirm}
              disabled={cleaningUp}
            >
              {cleaningUp ? 'Cleaning...' : 'Cleanup'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Message Modal (Success/Error) */}
      <Modal
        isOpen={!!messageModal}
        onClose={() => setMessageModal(null)}
        title={messageModal?.type === 'success' ? 'Success' : 'Error'}
        size="small"
      >
        <div style={{ padding: '20px' }}>
          <p>{messageModal?.message}</p>
          <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
            <button
              className="btn btn-primary"
              onClick={() => setMessageModal(null)}
            >
              OK
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ProtocolConfiguration;
